import {
  ColumnVisibilityState,
  ColumnReorderState,
  SortState,
  ColumnFilter,
} from 'types/table.types';

/**
 * Format table query parameters to send to backend
 */
export const formatTableQuery = (params: any) => {
  const formatted: any = {
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 20,
  };

  if (params.search) formatted.search = params.search;
  if (params.sortBy) formatted.sortBy = params.sortBy;
  if (params.sortDirection) formatted.sortDirection = params.sortDirection;

  // Add custom filters
  Object.keys(params).forEach((key) => {
    if (!['pageNumber', 'pageSize', 'search', 'sortBy', 'sortDirection'].includes(key)) {
      formatted[key] = params[key];
    }
  });

  return formatted;
};

/**
 * Convert TanStack sorting to backend format
 */
export const convertSortingToBackendFormat = (sorting: SortState[]) => {
  if (!sorting || sorting.length === 0) return { sortBy: undefined, sortDirection: 'asc' };

  const primarySort = sorting[0];
  return {
    sortBy: primarySort?.id,
    sortDirection: primarySort?.desc ? 'desc' : 'asc',
  };
};

/**
 * Save column visibility to localStorage
 */
export const saveColumnVisibility = (key: string, visibility: ColumnVisibilityState) => {
  try {
    localStorage.setItem(`table_visibility_${key}`, JSON.stringify(visibility));
  } catch (error) {
    console.error('Failed to save column visibility:', error);
  }
};

/**
 * Load column visibility from localStorage
 */
export const loadColumnVisibility = (key: string): ColumnVisibilityState | null => {
  try {
    const stored = localStorage.getItem(`table_visibility_${key}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load column visibility:', error);
    return null;
  }
};

/**
 * Save column order to localStorage
 */
export const saveColumnOrder = (key: string, order: string[]) => {
  try {
    localStorage.setItem(`table_order_${key}`, JSON.stringify(order));
  } catch (error) {
    console.error('Failed to save column order:', error);
  }
};

/**
 * Load column order from localStorage
 */
export const loadColumnOrder = (key: string): string[] | null => {
  try {
    const stored = localStorage.getItem(`table_order_${key}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load column order:', error);
    return null;
  }
};

/**
 * Export table data to CSV
 */
export const exportToCSV = <T extends Record<string, any>>(
  data: T[],
  columns: string[],
  filename = 'export.csv',
) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  const headers = columns;
  const rows = data.map((item) => columns.map((col) => JSON.stringify(item[col] ?? '')));

  const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get accessible column key
 */
export const getAccessorKey = (column: any): string => {
  return typeof column.accessorKey === 'string'
    ? column.accessorKey
    : typeof column.id === 'string'
    ? column.id
    : '';
};

/**
 * Build query string from table state
 */
export const buildQueryString = (params: Record<string, any>): string => {
  const queryParts: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => queryParts.push(`${key}=${encodeURIComponent(v)}`));
      } else {
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
    }
  });

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
};

/**
 * Parse query string to table state
 */
export const parseQueryString = (searchParams: URLSearchParams) => {
  return {
    pageNumber: parseInt(searchParams.get('pageNumber') || '1', 10),
    pageSize: parseInt(searchParams.get('pageSize') || '20', 10),
    search: searchParams.get('search') || undefined,
    sortBy: searchParams.get('sortBy') || undefined,
    sortDirection: (searchParams.get('sortDirection') as 'asc' | 'desc') || 'asc',
  };
};

/**
 * Merge default column visibility
 */
export const getDefaultColumnVisibility = (columns: any[], storageKey?: string) => {
  const stored = storageKey ? loadColumnVisibility(storageKey) : null;

  if (stored) return stored;

  return columns.reduce((acc, col) => {
    const key = getAccessorKey(col);
    if (key) {
      acc[key] = true;
    }
    return acc;
  }, {} as ColumnVisibilityState);
};

/**
 * Format bytes to human readable format
 */
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Truncate string
 */
export const truncateString = (str: string, length: number): string => {
  if (!str) return '';
  return str.length > length ? str.substring(0, length) + '...' : str;
};
